/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import {AdobeMetricsRuntime} from './AdobeMetricsRuntime';
import {RecordType} from '@exc/metrics';

const ELEMENT_ATTRIBUTES = [
  'id', 'class',
  'aria-label',
  'data-test-id',
  'data-omega-element', 'data-omega-feature', 'data-omega-feature-default', 'data-omega-widget', 'data-omega-widget-default'
];
const LOGGER_NAME = 'exc.metrics-browser-runtime.interactions';

const getElementPath = (node: HTMLElement, attributesToTrack: string[]): any[] => {
  let currNode = node;
  const result: any[] = [];
  while (currNode && currNode.nodeType === Node.ELEMENT_NODE) {
    const attributes: any = {};
    attributesToTrack.forEach((attribute: string) => {
      if (currNode.hasAttribute(attribute)) {
        attributes[attribute] = currNode.getAttribute(attribute);
      }
    });
    let sibling = currNode.previousElementSibling;
    let index = 0;
    while (sibling) {
      if (sibling.nodeType === Node.ELEMENT_NODE && sibling.tagName === currNode.tagName) {
        index++;
      }
      sibling = sibling.previousElementSibling;
    }
    result.push({attributes, index, tagName: currNode.tagName});
    currNode = currNode.parentElement as HTMLElement;
  }
  return result;
};

function extractPrimitiveAttributes(event: any): { [key: string]: any } {
  const result: { [key: string]: any } = {};

  for (const attribute in event) {
    if (typeof event[attribute] !== 'object' && typeof event[attribute] !== 'function') {
      result[attribute] = event[attribute];
    }
  }

  return result;
}

const handleEventFn = (
  e: Event,
  adobeMetrics: AdobeMetricsRuntime,
  win: Window & typeof globalThis
): void => {
  if (e.type === 'keydown' && (e as KeyboardEvent).key !== 'Enter' && (e as KeyboardEvent).key !== 'NumpadEnter') {
    return;
  }
  // instanceof must check against the correct global context.
  if (!(e.target instanceof win.HTMLElement)) {
    return;
  }

  const targetElement = e.target as HTMLElement;

  const data = {
    elementPath: getElementPath(targetElement, ELEMENT_ATTRIBUTES),
    event: extractPrimitiveAttributes(e),
    language: adobeMetrics.user?.language
  };

  adobeMetrics.write({
    data,
    event: e.type,
    name: LOGGER_NAME,
    recordType: RecordType.USER_EVENT
  });

};

const init = (adobeMetrics: AdobeMetricsRuntime, g: Window & typeof globalThis, mode?: string): void => {
  const handleEvent = (e: MouseEvent | KeyboardEvent): void => handleEventFn(e, adobeMetrics, g);

  if (mode !== 'off') {
    g.addEventListener('pointerup', handleEvent, {capture: true});
    g.addEventListener('keydown', handleEvent, {capture: true});
  }
};

const MetricsInteractionIntercept = {init};

export default MetricsInteractionIntercept;
